import React, { ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import {
  Button,
  ButtonColor,
  ButtonSize,
  LegacyLoadingSpinner as LoadingSpinner,
} from '@videoblocks/storywind';

import {
  ADOBE_PLUGIN_LANDING_PAGE_RELATIVE_PATH,
  ADOBE_PLUGIN_MARKETPLACE_URL,
} from '../../../Enterprise/AdobeConstants';
import { showPopUp } from '../../../PopUps/actions/PopUpsActions';
import CreateNewFolderModal from '../../../PopUps/components/CreateNewFolderModal';
import { CREATE_NEW_FOLDER_MODAL } from '../../../PopUps/components/types';
import { selectAuthPermissions } from '../../../auth/AuthSelectors';
import { addNewFolderWithStockItem } from '../../MemberBins/actions/FoldersActions';
import {
  selectIsInSharedFoldersBeta,
  selectIsLoadingFolders,
  selectMemberBins,
} from '../../MemberBins/selectors/foldersSelectors';
import { Folder } from '../types';
import FolderCardsGridContainer from './FolderCardsGrid';
import MyFoldersEnterpriseControls from './MyFoldersEnterpriseControls';
import MyFoldersSharedFoldersDisclaimers from './MyFoldersSharedFoldersDisclaimers';
import MyFoldersTopLevelControls from './MyFoldersTopLevelControls';

export interface MyFoldersPagePropTypes {
  isInSharedFoldersBeta: boolean;
  isLoading: boolean;
  bins: Folder[];
  dispatch: ThunkDispatch<any, any, any>;
}

export const MyFoldersPage = ({
  isLoading,
  isInSharedFoldersBeta,
  bins = [],
  dispatch,
}: MyFoldersPagePropTypes): ReactElement => {
  const [filterKeyword, setFilterKeyword] = useState<string>(null);
  const [showingPrivateFolders, setShowingPrivateFolders] =
    useState<boolean>(true);
  const [showingOnlyOwnedFolders, setShowingOnlyOwnedFolders] =
    useState<boolean>(false);
  const { pproPluginEnabled } = useSelector(selectAuthPermissions);
  const [showPProDialog, setShowPProDialog] = useState<boolean>(
    window.localStorage
      ? localStorage.getItem('showPProPluginDialog') === null
      : false
  );

  const foldersToRender = bins
    .filter((folder) => {
      if (showingPrivateFolders) {
        return !folder.isShared;
      }

      return (
        folder.isShared && (!showingOnlyOwnedFolders || folder.isOwnedByMe)
      );
    })
    .filter((folder) => {
      return (
        !filterKeyword ||
        folder.name.toLowerCase().includes(filterKeyword.toLowerCase())
      );
    });

  const openNewFolderModal = () => {
    dispatch(
      showPopUp(CREATE_NEW_FOLDER_MODAL, {
        title: 'Create a new folder',
        buttonText: 'Create',
        isInSharedFoldersBeta: isInSharedFoldersBeta,
        buttonAction: (newFolderName, folderAccessControl) => {
          dispatch(
            addNewFolderWithStockItem(newFolderName, [], folderAccessControl)
          );
        },
      })
    );
  };

  const closePremiereProPluginDialog = () => {
    setShowPProDialog(false);
    if (window.localStorage) {
      localStorage.setItem('showPProPluginDialog', 'false');
    }
  };

  const adobeUrl = pproPluginEnabled
    ? ADOBE_PLUGIN_MARKETPLACE_URL
    : ADOBE_PLUGIN_LANDING_PAGE_RELATIVE_PATH;

  return (
    <div>
      <div className="flex flex-wrap justify-between">
        {showPProDialog && (
          <div className="py-3 px-4 bg-gray-200 w-full rounded">
            <div className="flex justify-between items-start">
              <div className="flex">
                <img
                  src="/assets/common/images/home-v2/icons/ppro-circle.png"
                  alt="Premiere Pro Icon"
                  className="mr-3 w-9 h-9"
                />
                <img
                  src="/assets/common/images/home-v2/icons/ae-circle.png"
                  alt="After Effects Icon"
                  className="mr-3 w-9 h-9"
                />
                <a
                  target="_blank"
                  href={adobeUrl}
                  className="leading-5 font-semibold text-base text-black flex items-center min-h-9"
                >
                  Find, download, and organize content in your editor with our
                  Creative Cloud plugin
                </a>
              </div>
              <div className="flex ml-4">
                <Button
                  href={adobeUrl}
                  target="_blank"
                  color={ButtonColor.Secondary}
                  size={ButtonSize.Small}
                  className="whitespace-nowrap min-h-9 flex items-center"
                  onClick={() =>
                    Telemetry.increment('ppro.site-hook.folders-banner')
                  }
                >
                  Learn More &#8599;
                </Button>
                <div
                  role="button"
                  tabIndex={0}
                  className="ml-4 cursor-pointer"
                  onClick={closePremiereProPluginDialog}
                  onKeyDown={closePremiereProPluginDialog}
                >
                  <img
                    className="min-w-6 min-h-8"
                    src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/sb-close-black.svg`}
                    alt="Close"
                    width={24}
                    height={24}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col w-full px-4 mx-auto max-w-screen-2xl">
          <div className="flex flex-wrap p-4 w-full justify-between">
            <h1>{showingPrivateFolders ? 'My Folders' : 'Shared Folders'}</h1>

            <MyFoldersTopLevelControls
              onSearch={(event) => {
                setFilterKeyword(event.target.value);
              }}
              onCreateNewFolder={openNewFolderModal}
            />
          </div>

          {isInSharedFoldersBeta && !showingPrivateFolders && (
            <div className="p-4">
              <MyFoldersSharedFoldersDisclaimers />
            </div>
          )}
        </div>
      </div>

      {isInSharedFoldersBeta && (
        <div className="flex flex-col w-full px-4 mx-auto max-w-screen-2xl">
          <div className="mt-4 p-4">
            <MyFoldersEnterpriseControls
              showingPrivateFolders={showingPrivateFolders}
              onTogglePublicPrivateFolders={() =>
                setShowingPrivateFolders(!showingPrivateFolders)
              }
              showingOnlyOwnedFolders={showingOnlyOwnedFolders}
              onToggleOnlyOwnedFolders={() =>
                setShowingOnlyOwnedFolders(!showingOnlyOwnedFolders)
              }
            />
          </div>
        </div>
      )}

      <div className="flex flex-col w-full px-4 mx-auto max-w-screen-2xl">
        {isLoading ? (
          <div className="my-4 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="px-4 my-4 min-h-screen">
            <FolderCardsGridContainer folders={foldersToRender} />
          </div>
        )}
      </div>

      <CreateNewFolderModal />
    </div>
  );
};

const MyFoldersPageContainer = (): ReactElement => {
  const dispatch = useDispatch();
  const isInSharedFoldersBeta = useSelector(selectIsInSharedFoldersBeta);
  const bins = useSelector(selectMemberBins);
  const isLoading = useSelector(selectIsLoadingFolders);
  return (
    <MyFoldersPage
      dispatch={dispatch}
      isInSharedFoldersBeta={isInSharedFoldersBeta}
      isLoading={isLoading}
      bins={bins}
    />
  );
};

export default MyFoldersPageContainer;
